import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import type { EfetivacaoResponse } from '@/core/api/sicom-api/servicos/shared/efetivacao.model';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

import type { PrePrestadorConsultaResponse, PrePrestadorRequest, PrePrestadorResponse } from './pre-prestador.model';

@Injectable({ providedIn: 'root' })
export class PrePrestadorService extends BaseService {
    readonly PATH = SICOM_PATHS.servicos.pessoaJuridica.prePrestador;

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    consultar(params: PrePrestadorRequest, context?: HttpContext): Observable<PrePrestadorResponse> {
        const httpParams = new HttpParams().set('numeroProtocolo', params.numeroProtocolo).set('anoProtocolo', params.anoProtocolo).set('uf_crm', params.ufCrm);
        return this.http.get<PrePrestadorConsultaResponse>(this.PATH, { params: httpParams, context }).pipe(map((response) => this.normalizarConsultaResponse(response)));
    }

    consultarAbertos(ufCrm: string, context?: HttpContext): Observable<Record<string, unknown>[]> {
        const httpParams = new HttpParams().set('uf_crm', ufCrm);
        return this.http.get<Record<string, unknown>[]>(`${this.PATH}/abertos`, { params: httpParams, context });
    }

    atualizarCadastro(dto: PrePrestadorResponse, context?: HttpContext): Observable<void> {
        return this.http.put<void>(`${this.PATH}/atualizarCadastro`, dto, { context });
    }

    efetivarSolicitacao(dto: PrePrestadorResponse, context?: HttpContext): Observable<EfetivacaoResponse> {
        return this.http.put<EfetivacaoResponse>(`${this.PATH}/efetivarSolicitacao`, dto, { context });
    }

    /**
     * Inscrição de ofício, por incompletude de registro — o único caminho que efetiva sem pagamento.
     *
     * <p>Viaja num subpath PRÓPRIO, e não no do fluxo comum, porque é o path exato que sustenta a
     * permissão separada no backend: unificar os dois endereços faria a role perder o efeito. Quem
     * confere prazo de inércia e pendência financeira é o servidor, nunca o cliente.</p>
     */
    efetivarSolicitacaoExOfficio(dto: PrePrestadorResponse, context?: HttpContext): Observable<EfetivacaoResponse> {
        return this.http.put<EfetivacaoResponse>(`${this.PATH}/efetivarSolicitacaoExOfficio`, dto, { context });
    }

    cancelarSolicitacao(dto: PrePrestadorResponse, motivo?: string, context?: HttpContext): Observable<void> {
        const params = motivo ? new HttpParams().set('motivo', motivo) : undefined;
        return this.http.put<void>(`${this.PATH}/cancelarSolicitacao`, dto, { params, context });
    }

    private normalizarConsultaResponse(response: PrePrestadorConsultaResponse): PrePrestadorResponse {
        return Array.isArray(response) ? (response[0] ?? {}) : response;
    }
}
